/*
  LUA Complementos — slider de imágenes en tarjetas de producto.
  Custom element <product-item-media-slider>: scroll-snap con deslizamiento
  táctil en móvil, bucle infinito mediante clones del primer y último slide,
  y flechas laterales en todos los tamaños. En escritorio, al pasar el ratón
  se avanza a la segunda imagen (equivalente al hover original del tema) si
  el elemento tiene el atributo data-hover-advance.
*/
(() => {
  class ProductItemMediaSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;

      const slides = Array.from(this.track.children);
      this.count = slides.length;
      if (this.count < 2) return;

      this.userInteracted = false;

      // Clones para el bucle infinito: [clon-último, 1, 2, ..., n, clon-primero]
      const firstClone = slides[0].cloneNode(true);
      const lastClone = slides[this.count - 1].cloneNode(true);

      [firstClone, lastClone].forEach((clone) => {
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
      });

      this.track.appendChild(firstClone);
      this.track.insertBefore(lastClone, this.track.firstChild);

      // Posicionamos el scroll en el primer slide real en cuanto el track
      // tenga anchura (puede medir 0 si aún no se ha pintado).
      this.positioned = false;
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.positioned && this.track.clientWidth > 0) {
          this.positioned = true;
          this.jumpTo(1);
        }
      });
      this.resizeObserver.observe(this.track);

      // Al terminar un desplazamiento (dedo o flecha), si hemos quedado sobre
      // un clon saltamos sin animación al slide real equivalente.
      this.settleTimer = null;
      this.track.addEventListener(
        'scroll',
        () => {
          clearTimeout(this.settleTimer);
          this.settleTimer = setTimeout(() => this.normalize(), 90);
        },
        { passive: true }
      );

      const prevButton = this.querySelector('[data-prev]');
      const nextButton = this.querySelector('[data-next]');

      if (prevButton) {
        prevButton.addEventListener('click', () => {
          this.userInteracted = true;
          this.step(-1);
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          this.userInteracted = true;
          this.step(1);
        });
      }

      // Hover en escritorio: avanza a la segunda imagen y vuelve a la primera
      // al salir, mientras el usuario no haya usado las flechas.
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      if (canHover && this.hasAttribute('data-hover-advance')) {
        this.addEventListener('mouseenter', () => {
          if (!this.userInteracted && this.currentIndex() === 1) {
            this.step(1);
          }
        });

        this.addEventListener('mouseleave', () => {
          if (!this.userInteracted) {
            this.track.scrollTo({ left: this.slideWidth(), behavior: 'smooth' });
          }
        });
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      clearTimeout(this.settleTimer);
    }

    slideWidth() {
      return this.track.clientWidth;
    }

    currentIndex() {
      return Math.round(this.track.scrollLeft / this.slideWidth());
    }

    jumpTo(index) {
      this.track.scrollLeft = index * this.slideWidth();
    }

    normalize() {
      const width = this.slideWidth();
      if (!width) return;

      const index = this.currentIndex();

      if (index === 0) {
        // Clon del último → último real
        this.jumpTo(this.count);
      } else if (index === this.count + 1) {
        // Clon del primero → primero real
        this.jumpTo(1);
      }
    }

    step(direction) {
      const width = this.slideWidth();
      if (!width) return;

      this.normalize();
      this.track.scrollBy({ left: direction * width, behavior: 'smooth' });
    }
  }

  if (!customElements.get('product-item-media-slider')) {
    customElements.define('product-item-media-slider', ProductItemMediaSlider);
  }
})();
